import { useCallback, useEffect, useMemo, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment, Box, useMediaQuery, useTheme } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import {
  GridColDef,
  GridPaginationModel,
  GridToolbar,
  GridToolbarContainer,
} from "@mui/x-data-grid-premium";
import { useNavigate } from "react-router-dom";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiPrintButton from "src/components/core-components/mui/buttons/ByzMuiPrintButton";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { MedcardGpdIds } from "src/enums/MedcardGpdIds";
import { fetchOpenedMedcardsRows } from "src/modules/medcard/opened-medcars/openedMedcardsFetch";
import {
  OPENED_MEDCARDS_DEFAULT_PAGE_SIZE,
  OPENED_MEDCARDS_PAGE_SIZE_OPTIONS,
} from "src/modules/medcard/opened-medcars/openedMedcardsGridConfig";
import type { MedcardRow } from "src/models/OpenedMedcars";
import { useTranslate } from "src/locales";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { UserOtherPrivilegesEnum } from "src/modules/auth/userOtherPrivilegesSlice";

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ color: "#00B8D9", p: 0.5, ml: -1 }}>
      <GridToolbar />
    </GridToolbarContainer>
  );
}

type OpenedMedcardsPanelProps = {
  patientId?: number;
  upn?: string;
  gender?: string;
  onOpenFullModal: () => void;
};

export default function OpenedMedcardsPanel({
  patientId,
  upn,
  gender,
  onOpenFullModal,
}: Readonly<OpenedMedcardsPanelProps>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [inputSearchValue, setInputSearchValueValue] = useState("");
  const [lab, setLab] = useState(false);
  const [radiology, setRadiology] = useState(false);
  const [examination, setExamination] = useState(false);
  const [other, setOther] = useState(false);
  const [patientFilterType, setPatientFilterType] = useState("1");
  const [rows, setRows] = useState<MedcardRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: OPENED_MEDCARDS_DEFAULT_PAGE_SIZE,
  });

  const patientIdStr = typeof patientId === "number" ? String(patientId) : undefined;

  useEffect(() => {
    setPaginationModel({ page: 0, pageSize: OPENED_MEDCARDS_DEFAULT_PAGE_SIZE });
  }, [patientIdStr]);

  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [inputSearchValue, lab, radiology, examination, other, patientFilterType]);

  const handleOpenCard = useCallback(
    (row: MedcardRow) => {
      const isReportCard =
        row.gpdid === MedcardGpdIds.Lab ||
        row.gpdid === MedcardGpdIds.Radiology ||
        row.gpdid === MedcardGpdIds.Examination;

      if (isReportCard) {
        onOpenFullModal();
        return;
      }
      if (row.url) {
        navigate(row.url);
      }
    },
    [navigate, onOpenFullModal]
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "Open",
        headerName: t("opened-medcards.open"),
        renderCell(params) {
          return (
            <OtherPrivilegeGuard
              uopmid={UserOtherPrivilegesEnum.MedicalCards}
              privid={params.row.orId}
              fallback={
                <ByzMuiButton
                  sx={{
                    opacity: 0.5,
                    width: "100%",
                    height: "30px",
                    minHeight: "30px",
                    padding: "2px 8px",
                  }}
                  variant="contained"
                  color="primary"
                  disabled
                  onClick={() => {}}
                >
                  {t("opened-medcards.open")}
                </ByzMuiButton>
              }
            >
              <ByzMuiButton
                onClick={() => handleOpenCard(params.row)}
                sx={{
                  width: "100%",
                  height: "30px",
                  minHeight: "30px",
                  padding: "2px 8px",
                }}
                variant="contained"
                color="primary"
              >
                {t("opened-medcards.open")}
              </ByzMuiButton>
            </OtherPrivilegeGuard>
          );
        },
        width: 90,
      },
      { field: "CardName", headerName: t("opened-medcards.card-name"), flex: 2 },
      { field: "Status", headerName: t("opened-medcards.status"), flex: 1 },
      { field: "ProcessTime", headerName: t("opened-medcards.process-time"), flex: 1 },
    ],
    [t, handleOpenCard]
  );

  const fetchPatientMedcards = useCallback(async () => {
    if (!patientIdStr) return;

    setLoading(true);
    try {
      const formattedRows = await fetchOpenedMedcardsRows(
        patientIdStr,
        patientFilterType,
        upn,
        gender,
        t
      );
      setRows(formattedRows);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [patientIdStr, patientFilterType, upn, gender, t]);

  useEffect(() => {
    if (!patientIdStr) {
      setRows([]);
      return;
    }
    fetchPatientMedcards();
  }, [patientIdStr, patientFilterType, fetchPatientMedcards]);

  const getFilteredRows = () => {
    let filtered = [...rows];

    if (inputSearchValue.length >= 2) {
      filtered = filtered.filter(
        (row) =>
          row.CardName.toLowerCase().includes(inputSearchValue.toLowerCase()) ||
          row.Status.toLowerCase().includes(inputSearchValue.toLowerCase())
      );
    }

    if (lab || radiology || examination || other) {
      filtered = filtered.filter((row) => {
        const gpdid = row.gpdid;

        const matchesLab = lab && gpdid === MedcardGpdIds.Lab;
        const matchesRadiology = radiology && gpdid === MedcardGpdIds.Radiology;
        const matchesExamination = examination && gpdid === MedcardGpdIds.Examination;
        const matchesOther =
          other && (gpdid === null || (gpdid !== MedcardGpdIds.Lab && gpdid !== MedcardGpdIds.Radiology && gpdid !== MedcardGpdIds.Examination));

        return matchesLab || matchesRadiology || matchesExamination || matchesOther;
      });
    }

    return filtered;
  };

  if (!patientIdStr) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: "18px",
        width: "100%",
        p: 2,
        background: "#fff",
        borderRadius: "12px",
        boxShadow: "0px 1px 8px rgba(0, 0, 0, 0.08)",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minHeight: 360,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ flex: 1, minWidth: "200px", maxWidth: "400px" }}>
          <ByzMuiTextField
            placeholder={`${t("medcard")} ${t("search")}`}
            type="search"
            variant="outlined"
            fullWidth
            size="small"
            value={inputSearchValue}
            onChange={(e) => setInputSearchValueValue(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
              sx: {
                borderRadius: "16px",
                backgroundColor: "#f4f7fe",
                "& fieldset": { border: "none" },
                ml: 2,
              },
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", ml: { xs: 0, sm: 4 }, gap: 1 }}>
          <FormControlLabel
            control={
              <RadioGroup row value={patientFilterType} onChange={(e) => setPatientFilterType(e.target.value)}>
                <FormControlLabel value="1" control={<Radio size="small" />} label={t("opened-medcards.arrivals")} />
                <FormControlLabel value="0" control={<Radio size="small" />} label={t("opened-medcards.allarrivals")} />
              </RadioGroup>
            }
            label=""
            sx={{ m: 0, mr: 2 }}
          />

          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", ml: { xs: 0, sm: 2 } }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={lab} onChange={() => setLab(!lab)} />}
              label={t("opened-medcards.laboratory")}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={radiology} onChange={() => setRadiology(!radiology)} />}
              label={t("opened-medcards.radiology")}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={examination} onChange={() => setExamination(!examination)} />}
              label={t("opened-medcards.inspection")}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={other} onChange={() => setOther(!other)} />}
              label={t("opened-medcards.other")}
            />
          </Box>

          <Box sx={{ borderLeft: "1px solid #ccc", pl: 2, ml: 2 }}>
            <ByzMuiPrintButton disabled type={1} sx={{ ml: 1, width: "36px", height: "36px", minWidth: "36px", padding: "5px" }} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: "100%", flex: 1, minHeight: 320, overflowX: isMobile ? "auto" : "hidden", overflowY: "hidden" }}>
        <Box sx={{ minWidth: isMobile ? 640 : "100%", height: "100%" }}>
          <ByzMuiDataGrid
            rows={getFilteredRows()}
            customToolbar={CustomToolbar}
            columns={columns}
            paginationMode="client"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[...OPENED_MEDCARDS_PAGE_SIZE_OPTIONS]}
            getRowId={(row) => row.id}
            loading={loading}
            sx={{ height: "100%", minHeight: 320 }}
            initialState={{ density: "compact" }}
            whiteGrid
          />
        </Box>
      </Box>
    </Box>
  );
}
